/** Pure view-model helpers for the Skill Check Sheets tab (no React, no API). */

import { totalPoints } from "../../../components/checklistScoring";

const DAY_MS = 86400000;

export const pluralize = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "today", "yesterday", "8 days ago", "3 mo ago"; "—" when the date is missing or invalid. */
export function relativeUpdated(dateStr, now = Date.now()) {
  if (!dateStr) return "—";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "—";
  const days = Math.max(0, Math.floor((now - then) / DAY_MS));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

export const skillLabel = (item, index) =>
  item?.criteria || item?.title || item?.description || `Step ${index + 1}`;

export function initials(name) {
  return (
    String(name || "")
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"
  );
}

/** Total skills in a bank sheet's `items` JSON (flat list, or sections that each hold `items`). */
export function parseBankSkills(itemsJson) {
  try {
    const data = typeof itemsJson === "string" ? JSON.parse(itemsJson) : itemsJson || [];
    if (!Array.isArray(data)) return [];
    if (data.length > 0 && data[0]?.items) return data.flatMap((s) => (Array.isArray(s.items) ? s.items : []));
    return data;
  } catch {
    return [];
  }
}

// passingScore is in POINTS, so compare it with total points, not the number of items.
function passInfo(passingScore, total) {
  if (passingScore == null) return { required: null, threshold: null };
  return {
    required: `${passingScore} of ${total}`,
    threshold: total > 0 ? `${Math.round((passingScore / total) * 100)}%` : null,
  };
}

/** Per-student completion rows for the first `limit` students that have progress on a checklist. */
export function studentRows(progressStudents, studentMap, checklistId) {
  return (progressStudents || []).map((sp) => {
    const stu = studentMap?.[sp.studentId];
    const name = stu ? `${stu.firstName ?? ""} ${stu.lastName ?? ""}`.trim() : `Student ${sp.studentId}`;
    const done = sp.percentComplete >= 100;
    // Peer prerequisite met (peer approval or a PASS practice with a partner), only meaningful when peer is required.
    const row = Array.isArray(sp.checklists) ? sp.checklists.find((c) => c.checklistId === checklistId) : null;
    const peerApproved = Boolean(row?.peerAssessmentRequired && row?.peerPrereqMet);
    return {
      id: sp.studentId,
      name,
      initials: initials(name),
      done,
      peerApproved,
      label: done ? `${sp.passed}/${sp.passed + sp.failed} · done` : `${sp.percentComplete ?? 0}%`,
    };
  });
}

/** Model for a course-authored checklist. */
export function buildChecklistModel(cl, { progressStudents = [], enrolled = 0, studentMap = {}, now } = {}) {
  const items = Array.isArray(cl.items) ? cl.items : [];
  const skillCount = items.length || cl.itemCount || 0;
  const completed = progressStudents.filter((s) => s.passed > 0 || s.percentComplete >= 100).length;
  return {
    kind: "checklist",
    id: cl.id,
    title: cl.title,
    description: cl.description || "",
    published: cl.enabled !== false,
    fromBank: Boolean(cl.sourceSkillSheetBankId),
    requiredForPass: Boolean(cl.requiredForPass),
    inLearningPlan: Boolean(cl.inLearningPlan),
    peerAssessmentRequired: Boolean(cl.peerAssessmentRequired),
    skillCount,
    skills: items.map((it, i) => ({ label: skillLabel(it, i), critical: Boolean(it.isCritical) })),
    ...passInfo(cl.passingScore, items.length ? totalPoints(items) : skillCount),
    updated: relativeUpdated(cl.updatedAt, now),
    completed,
    enrolled,
    pct: enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0,
    students: studentRows(progressStudents, studentMap, cl.id),
  };
}

/** Model for a Knowledge Bank sheet assigned to the course. Bank sheets carry no progress data. */
export function buildBankModel(assignment, { now } = {}) {
  const sheet = assignment.sheet || {};
  const skills = parseBankSkills(sheet.items);
  return {
    kind: "bank",
    id: assignment.id,
    title: sheet.title || `Bank Sheet #${assignment.skillSheetId}`,
    description: sheet.description || "",
    published: Boolean(assignment.enabled),
    fromBank: true,
    requiredForPass: false,
    inLearningPlan: Boolean(assignment.inLearningPlan),
    peerAssessmentRequired: Boolean(sheet.peerAssessmentRequired),
    skillCount: skills.length,
    skills: skills.map((it, i) => ({ label: skillLabel(it, i), critical: Boolean(it?.isCritical) })),
    required: null,
    threshold: null,
    updated: relativeUpdated(sheet.updatedAt, now),
    completed: 0,
    enrolled: 0,
    pct: 0,
    students: [],
  };
}
